import { useEffect } from 'react'
import {
  PAGE_SEO,
  applyPageSeo,
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildPlaceSchema,
  buildWebPageSchema,
  buildWebSiteSchema,
} from '../utils/seo'

/**
 * SEO de page (meta + JSON-LD). À monter une fois par route indexable.
 *
 * @param {{
 *   page?: keyof typeof PAGE_SEO,
 *   title?: string,
 *   description?: string,
 *   path?: string,
 *   keywords?: string,
 *   noindex?: boolean,
 *   image?: string,
 *   type?: string,
 *   breadcrumbs?: Array<{ name: string, path: string }>,
 *   jsonLd?: object[],
 *   includeSiteGraph?: boolean,
 * }} props
 */
export default function Seo({
  page,
  title,
  description,
  path,
  keywords,
  noindex,
  image,
  type,
  breadcrumbs,
  jsonLd = [],
  includeSiteGraph = false,
}) {
  const base = page ? PAGE_SEO[page] : null
  const resolved = {
    title: title || base?.title || 'Nature & Gîtes',
    description: description || base?.description || '',
    path: path || base?.path || '/',
    keywords: keywords || base?.keywords,
    noindex: noindex ?? base?.noindex ?? false,
    image,
    type,
  }

  const breadcrumbsKey = JSON.stringify(breadcrumbs || [])
  const jsonLdKey = JSON.stringify(jsonLd || [])

  useEffect(() => {
    const graph = []

    if (includeSiteGraph) {
      graph.push(buildOrganizationSchema(), buildWebSiteSchema(), buildPlaceSchema())
    }

    graph.push(
      buildWebPageSchema({
        title: resolved.title,
        description: resolved.description,
        path: resolved.path,
      }),
    )

    if (breadcrumbs?.length) {
      graph.push(buildBreadcrumbSchema(breadcrumbs))
    }

    graph.push(...(jsonLd || []))

    return applyPageSeo({
      ...resolved,
      jsonLd: graph,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clés sérialisées pour stabilité
  }, [
    resolved.title,
    resolved.description,
    resolved.path,
    resolved.keywords,
    resolved.noindex,
    resolved.image,
    resolved.type,
    includeSiteGraph,
    breadcrumbsKey,
    jsonLdKey,
  ])

  return null
}
